/**
 * Navigation model: role labels and the command-palette catalog. Filtered per user against the
 * central access model (core/access.js).
 */
import React from 'react';
import { Bell, Building2, ChartPie, ClipboardCheck, ClipboardList, ClipboardPen, FileSignature, History, Layers, LayoutDashboard, ListChecks, Package, ReceiptText, ScanBarcode, ShieldCheck, Sparkles, Store, Tags, UserCheck, Users, UsersRound } from 'lucide-react';
import { canAccess } from '../../../core/access';
import { t } from '../../../company/labels';
import { getActiveCompany, isScreenEnabled } from '../../../company/session';
import { sidebarList } from './list';

// Display names for the roles that can sign in to the admin panel.
export const ROLE_LABELS = {
    admin: 'Administrador',
    asesor: 'Asesor',
    gerente: 'Gerente',
    client: 'Cliente',
    TradeMarketing: 'Trade Marketing',
    auditAnalyst: 'Análisis Auditor',
};

// Full command-palette catalog. Rendered per user by filtering against the
// central access model (see core/access.js) instead of hardcoding per-role lists. Labels are
// read once per page load (the company only changes with a full reload).
export const SEARCH_CATALOG = [
    { key: 'dashboard', label: 'Inicio', description: 'Panel principal', icon: <LayoutDashboard />, href: '/dashboard' },
    { key: 'clients', label: 'Clientes', description: 'Gestión de clientes', icon: <Building2 />, href: '/dashboard/clients' },
    { key: 'stores', label: 'Pisos de Venta', description: 'Gestión de tiendas', icon: <Store />, href: '/dashboard/stores' },
    { key: 'notifications', label: 'Notificaciones', description: 'Alertas y avisos', icon: <Bell />, href: '/dashboard/notifications' },
    { key: 'users', label: 'Usuarios', description: 'Gestión de usuarios', icon: <Users />, href: '/dashboard/users' },
    { key: 'permissions', label: 'Permisos', description: 'Pantallas y funciones por rol', icon: <ShieldCheck />, href: '/dashboard/permissions' },
    { key: 'price', label: 'Lista de precio', description: 'Gestión de precios', icon: <ReceiptText />, href: '/dashboard/price' },
    { key: 'distributor-prices', label: t('navDistributorPrices'), description: 'Precios de distribuidor', icon: <ClipboardList />, href: '/dashboard/distributor-prices' },
    { key: 'levantamiento-general', label: 'Levantamiento General', description: 'Captura de precios general', icon: <ScanBarcode />, href: '/dashboard/levantamiento-general' },
    { key: 'products', label: 'Productos', description: 'Catálogo central de productos', icon: <Package />, href: '/dashboard/products' },
    { key: 'segments', label: 'Segmentaciones', description: 'Segmentos de productos', icon: <Layers />, href: '/dashboard/segments' },
    { key: 'tags', label: 'Etiquetas', description: 'Gestión de etiquetas', icon: <Tags />, href: '/dashboard/tags' },
    { key: 'sos-analysis', label: 'Análisis SOS', description: 'Share of Shelf', icon: <ChartPie />, href: '/dashboard/sos-analysis' },
    { key: 'activity-logs', label: 'Registro de Actividad', description: 'Historial de acciones', icon: <History />, href: '/dashboard/activity-logs' },
    { key: 'promoters', label: t('promotores'), description: 'Trade Marketing', icon: <UsersRound />, href: '/dashboard/trade/promoters' },
    { key: 'auditors', label: 'Auditores', description: 'Trade Marketing', icon: <UserCheck />, href: '/dashboard/trade/auditors' },
    {
        key: 'auditor-field-options',
        label: 'Opciones de auditoría',
        description: 'Categorías y segmentos para auditores',
        icon: <ListChecks />,
        href: '/dashboard/trade/auditor-field-options'
    },
    {
        key: 'audit-console',
        label: 'Consola de auditoría',
        description: 'Analíticas y productos de auditoría',
        icon: <ClipboardCheck />,
        href: '/dashboard/trade/audit-console'
    },
    { key: 'polls', label: t('navPolls'), description: t('navPollsDescription'), icon: <ClipboardPen />, href: '/dashboard/trade/polls' },
    { key: 'contracts', label: 'Contrataciones', description: 'Trade Marketing', icon: <FileSignature />, href: '/dashboard/trade/contracts' },
    { key: 'campaigns', label: 'Campañas', description: 'Campañas de TM', icon: <Sparkles />, href: '/dashboard/trade/campaigns' },
];

// Search items with icons and descriptions, filtered by the access model.
export const getSearchItems = (userType, clientId) => {
    // Clients get a bespoke palette: "Mi Empresa" links to their own profile.
    if (userType === 'client') {
        const clientProfileHref = clientId
            ? `/dashboard/clients/client?id=${clientId}`
            : '/dashboard/clients/client';
        return [
            { key: 'my-client', label: 'Mi Empresa', description: 'Perfil de tu empresa', icon: <Building2 />, href: clientProfileHref },
            { key: 'stores', label: 'Pisos de Venta', description: 'Tus tiendas', icon: <Store />, href: '/dashboard/stores' },
            { key: 'promoters', label: t('promotores'), description: `${t('promotores')} de tus tiendas`, icon: <UsersRound />, href: '/dashboard/trade/promoters' },
        ].filter((item) => isScreenEnabled(item.key));
    }

    const extra = getActiveCompany().navigation?.extraNav || [];
    return [...SEARCH_CATALOG, ...extra].filter((item) => canAccess(userType, item.key));
};

/** Label path of a nav key, e.g. ['Precio', 'Levantamiento General'] — used for the top bar. */
export function navTrail(key, items = sidebarList, parents = []) {
    for (const item of items) {
        if (item.key === key) return [...parents, item.label];
        if (item.children) {
            const found = navTrail(key, item.children, [...parents, item.label]);
            if (found) return found;
        }
    }
    return null;
}
